import React from "react";
import { node, bool, string } from "prop-types";
import { twMerge } from "tailwind-merge";

import { useIsMounted } from "hooks/useIsMounted";
import { useCarousel } from "hooks/useCarousel";
import ArrowLeftIcon from "./Icons/ArrowLeft";
import ArrowRightIcon from "./Icons/ArrowRight";
import Icon from "./Icons/Icon";

const ScrollableContainer = ({
  children: _children,
  className,
  isVisible = true,
}) => {
  const children = _children.filter(Boolean);

  const isMounted = useIsMounted();

  const {
    childRefs,
    containerRef,
    handleArrowClick,
    isAtTheEnd,
    isOnTheStart,
    isScrollbarVisible,
  } = useCarousel({
    itemsCount: children.length,
    isMounted: isMounted && isVisible,
  });

  const isSingleItem = children.length === 1;

  return (
    <div
      className={twMerge(
        "relative w-full",
        !isSingleItem && isScrollbarVisible && "w-auto max-largeMobile:-mx-7",
        className?.outer
      )}
    >
      <div
        className={twMerge(
          "flex items-stretch gap-4 size-full pb-2 snap-x snap-proximity fancy-scrollbars",
          "before:content-[''] before:shrink-0 before:w-0 before:h-px before:pointer-events-none before:snap-start before:-mr-4",
          "after:content-[''] after:shrink-0 after:w-0 after:h-px after:pointer-events-none after:snap-end after:-ml-4",
          !isSingleItem &&
            isScrollbarVisible &&
            "before:w-5 before:mr-0 after:w-5 after:ml-0 scroll-pl-8 overflow-x-auto overflow-y-hidden",
          className?.inner
        )}
        ref={containerRef}
      >
        {children.map((child, idx) => {
          const isLast = idx === children.length - 1;
          const isFirst = idx === 0;
          const isNotFirstOrLast = !isFirst && !isLast;

          return (
            <div
              className={twMerge(
                "shrink-0",
                isNotFirstOrLast && "snap-start",
                isLast && "snap-none",
                isSingleItem && "w-full"
              )}
              key={idx}
              ref={node => {
                if (node) {
                  childRefs.current[idx] = node;
                  node.dataset.page = `${idx + 1}`;
                }
              }}
            >
              {child}
            </div>
          );
        })}
      </div>
      {children.length > 1 && isScrollbarVisible && (
        <>
          <button
            className="
              absolute left-0 top-11 z-1
              grid place-items-center size-8 p-1 bg-white rounded-full border border-greyLight
              text-primaryDark text-center cursor-pointer select-none transition duration-150
              hover:text-primaryOrange hover:border-primaryOrange hover:scale-105 active:scale-95
              disabled:opacity-50 disabled:bg-greyLight disabled:text-greyLigh disabled:pointer-events-none
            "
            disabled={isOnTheStart()}
            onClick={() => handleArrowClick(-1)}
          >
            <Icon className="text-sm size-4 pr-0.5" icon={<ArrowLeftIcon />} />
          </button>
          <button
            className="
              absolute right-0 top-11 z-1
              grid place-items-center size-8 p-1 bg-white rounded-full border border-greyLight
              text-primaryDark text-center cursor-pointer select-none transition duration-150
              hover:text-primaryOrange hover:border-primaryOrange hover:scale-105 active:scale-95
              disabled:opacity-50 disabled:bg-greyLight disabled:text-greyLigh disabled:pointer-events-none
            "
            disabled={isAtTheEnd()}
            onClick={() => handleArrowClick(1)}
          >
            <Icon className="text-sm size-4 pl-0.5" icon={<ArrowRightIcon />} />
          </button>
        </>
      )}
    </div>
  );
};

ScrollableContainer.propTypes = {
  children: node.isRequired,
  className: string,
  isDisabled: bool,
  isVisible: bool,
};

export default ScrollableContainer;
